// Data layer de Pre-lanzamientos — ADR-2026-09-06-lanzamientos-modelo-de-datos.
//
// Un lanzamiento junta PRODUCTOS (con una ficha editable que es del borrador, no
// del catálogo vivo) y, dentro de cada uno, las IMÁGENES generadas en Atlas que
// van a subir, en orden. Publicar reusa el pipeline del export a Tiendanube.
import { supabase } from "../../lib/supabase";
import { fetchInChunks } from "../../utils/supabaseChunkedIn";
import { assertSupportedUploadFormat, normalizeImageForUpload } from "../../utils/heicConvert";
import {
  esArchivoDeVideo,
  esNombreDeVideo,
  nombreDelPoster,
  posterDeVideo,
} from "../../utils/posterDeVideo";
// El tope de peso de un video es una política de TODA la app, no del chat: está
// ahí porque el chat fue el primero en subir videos. Se importa en vez de
// copiarse para que no queden dos topes que se contradigan.
import { MAX_BYTES_VIDEO, mensajeVideoGrande } from "../../chat-atlas/adjuntosChatAtlas";
import { idDeLanzamientoEnRuta } from "./agenteLanzamiento";
import { destinosDe, reglasDeDestinos, type CanalLanzamiento } from "./canales";
import type { DatosMlDeFicha } from "../../lib/mercadoLibrePublicarLogica";
import {
  problemasDeFicha,
  tnBodyDeProducto,
  type FichaProducto,
  type NivelDeCategoria,
  type TnProductoBody,
} from "./fichaTiendanube";
import { crearFilaDeGuardados } from "./filaDeGuardados";
import { paqueteInicialDesdeCatalogo } from "./paqueteDelCatalogo";

const GENERADAS_BUCKET = "generadas";
const SIGNED_URL_TTL = 3600;

/** Topes del backend del export (zod de `/v1/products/tiendanube-export`). */
export const MAX_PRODUCTOS_POR_LANZAMIENTO = 50;
export const MAX_IMAGENES_POR_PRODUCTO = 40;

// El canal vive en `canales.ts` con sus reglas; acá se re-exporta para que quien
// ya lo importaba de la API no tenga que cambiar.
export type { CanalLanzamiento } from "./canales";
export type EstadoLanzamiento = "borrador" | "publicando" | "publicado" | "con_errores";
export type EstadoProducto = "pendiente" | "publicando" | "publicado" | "error";
export type AccionProducto = "add" | "replace" | "create";

export interface Lanzamiento {
  id: string;
  nombre: string;
  canal: CanalLanzamiento;
  /**
   * Las tiendas a las que va: las elegidas si son varias, o `[canal]`
   * (ADR-2026-09-30-lanzamiento-a-varias-tiendas). Siempre tiene al menos una.
   */
  destinos: CanalLanzamiento[];
  estado: EstadoLanzamiento;
  fechaLanzamiento: string | null;
  brandId: string | null;
  createdAt: string;
  /** Contadores para las cards del listado. */
  productos: number;
  imagenes: number;
  /** Productos ya publicados y que fallaron: la barra de avance de la card. */
  publicados: number;
  conError: number;
  /**
   * Hasta tres miniaturas, la primera foto de los primeros productos. Solo con
   * `conPortadas`: firmar cuesta un pedido por foto y los selectores no las muestran.
   */
  portadas: string[];
}

export interface ImagenDeLanzamiento {
  id: string;
  /** `null` = la subió el usuario a mano; no hay fila de generación detrás. */
  imagenGeneradaId: string | null;
  fileName: string;
  /** De qué variante del CATÁLOGO salió la imagen (informativo). */
  varianteId: string | null;
  /** A qué variante del LANZAMIENTO se asignó. NULL = foto general. */
  lanzamientoVarianteId: string | null;
  orden: number;
  thumbUrl: string;
}

/** Una variante de la ficha (Color, Talle…). Ver `fichaTiendanube.ts`. */
export interface VarianteDeLanzamiento {
  id: string;
  orden: number;
  valores: string[];
  precio: string | null;
  costo: string | null;
  stock: string | null;
  sku: string | null;
  codigoBarras: string | null;
  peso: string | null;
  alto: string | null;
  ancho: string | null;
  profundidad: string | null;
}

export interface ProductoDeLanzamiento {
  id: string;
  /**
   * Lo que pide Mercado Libre y Tiendanube no (categoría, atributos, guía de talles…): columna
   * `lanzamientos_productos.mercadolibre` (ADR-2026-09-30-la-ficha-de-mercado-libre). null = nada todavía.
   */
  mercadolibre?: DatosMlDeFicha | null;
  /** Cómo le fue en cada tienda (lo anota el worker en `lanzamientos_publicaciones`). */
  porDestino?: Partial<Record<CanalLanzamiento, { estado: string; error: string | null }>>;
  /**
   * Producto del catálogo detrás de esta fila. `null` = fila BORRADOR (cargada por
   * Excel) que todavía no fue adoptada por un producto real
   * (ADR-2026-09-09-productos-de-lanzamiento-sin-catalogo).
   *
   * Una fila huérfana guarda datos y se edita como cualquier otra, pero no se publica:
   * sin producto no hay fotos, y `payloadDeExport` filtra lo que no tiene fotos. Se ata
   * por SKU cuando el producto llega con sus imágenes.
   */
  productoId: string | null;
  orden: number;
  nombre: string;
  descripcion: string | null;
  precio: string | null;
  stock: number | null;
  sku: string | null;
  accion: AccionProducto;
  estado: EstadoProducto;
  error: string | null;
  /** Del catálogo real, no de la ficha: decide si en el destino se crea o se actualiza. */
  tnProductId: number | null;
  imagenes: ImagenDeLanzamiento[];
  // ── Ficha completa (ADR-2026-09-06-lanzamientos-ficha-completa) ──
  costo: string | null;
  codigoBarras: string | null;
  /** Kilogramos. */
  peso: string | null;
  /** Centímetros. */
  alto: string | null;
  ancho: string | null;
  profundidad: string | null;
  marca: string | null;
  /** Separadas por coma, como las recibe Tiendanube. */
  tags: string | null;
  seoTitulo: string | null;
  seoDescripcion: string | null;
  /** Nombres de los atributos, en orden. Vacío = producto de una sola variante. */
  atributos: string[];
  variantes: VarianteDeLanzamiento[];
  /** Qué define cada variante. Apagado = usa el valor general del producto. */
  precioPorVariante: boolean;
  envioPorVariante: boolean;
  imagenesPorVariante: boolean;
  /** Ids de `categorias_tienda` a las que quedó atado en ESTE lanzamiento. */
  categorias: string[];
}

export interface LanzamientoDetalle extends Lanzamiento {
  productos_: ProductoDeLanzamiento[];
}

async function firmar(companyId: string, fileName: string, carpeta: "opt" | "full"): Promise<string> {
  const { data } = await supabase.storage
    .from(GENERADAS_BUCKET)
    .createSignedUrl(`${companyId}/${carpeta}/${fileName}`, SIGNED_URL_TTL);
  return data?.signedUrl || "";
}

/** Un `numeric` de Postgres puede llegar como número o como texto: la ficha
 *  siempre lo maneja como texto, que es lo que escribe un formulario. */
function aTexto(v: unknown): string | null {
  if (v === null || v === undefined || v === "") return null;
  return String(v);
}

/**
 * Miniatura con fallback a full: no todas las generadas tienen `opt/`.
 *
 * Un VIDEO no puede caer a `full/`: ahí está el mp4, y un `<img src="...mp4">`
 * es el ícono roto. Su miniatura es el póster que vive al lado con `.png`
 * (ADR-2026-09-15-un-video-subido-necesita-su-poster). Sin póster devuelve
 * vacío a propósito, y la pantalla dibuja un ícono de video.
 */
export async function firmarThumb(companyId: string, fileName: string): Promise<string> {
  if (esNombreDeVideo(fileName)) {
    return firmar(companyId, nombreDelPoster(fileName), "opt");
  }
  return (await firmar(companyId, fileName, "opt")) || (await firmar(companyId, fileName, "full"));
}

/** URL full firmada — es la que se le manda al destino, que baja la foto por su cuenta. */
export async function firmarFull(companyId: string, fileName: string): Promise<string> {
  return firmar(companyId, fileName, "full");
}

// ── Listado ────────────────────────────────────────────────────────────────

/**
 * Lanzamientos de la marca activa. Incluye los que no tienen marca (creados antes
 * de que existiera una, o desde una pantalla sin marca) para que no desaparezcan.
 */
/**
 * Los destinos guardados (`lanzamientos.destinos`, migración 20260930160000). Va en una consulta
 * aparte a propósito: si la migración todavía no está aplicada, falla SÓLO esta y los lanzamientos
 * se ven igual que siempre, con su `canal` (ADR-2026-09-30-lanzamiento-a-varias-tiendas).
 */
async function destinosGuardados(ids: string[]): Promise<Map<string, string[] | null>> {
  if (ids.length === 0) return new Map();
  const { data, error } = await supabase.from("lanzamientos").select("id, destinos").in("id", ids);
  if (error) return new Map();
  return new Map(((data || []) as Array<{ id: string; destinos: string[] | null }>).map((r) => [r.id, r.destinos]));
}

/**
 * Lo de Mercado Libre de cada producto y cómo le fue en cada tienda. Consultas aparte a propósito: sin
 * las migraciones (20260930160000 y 20260930150000) fallan solas y la ficha se ve como siempre
 * (ADR-2026-09-30-la-ficha-de-mercado-libre).
 */
async function extrasDeProductos(
  lanzamientoId: string,
): Promise<{ ml: Map<string, DatosMlDeFicha | null>; porDestino: Map<string, Record<string, { estado: string; error: string | null }>> }> {
  const ml = new Map<string, DatosMlDeFicha | null>();
  const porDestino = new Map<string, Record<string, { estado: string; error: string | null }>>();
  const [a, b] = await Promise.all([
    supabase.from("lanzamientos_productos").select("id, mercadolibre").eq("lanzamiento_id", lanzamientoId),
    supabase.from("lanzamientos_publicaciones").select("lanzamiento_producto_id, destino, estado, error").eq("lanzamiento_id", lanzamientoId),
  ]);
  if (!a.error) for (const r of (a.data || []) as Array<{ id: string; mercadolibre: DatosMlDeFicha | null }>) ml.set(r.id, r.mercadolibre ?? null);
  if (!b.error) {
    for (const r of (b.data || []) as Array<{ lanzamiento_producto_id: string; destino: string; estado: string; error: string | null }>) {
      const x = porDestino.get(r.lanzamiento_producto_id) ?? {};
      x[r.destino] = { estado: r.estado, error: r.error };
      porDestino.set(r.lanzamiento_producto_id, x);
    }
  }
  return { ml, porDestino };
}

/** Guarda lo de Mercado Libre de un producto. false = no se pudo (sin la migración, por ejemplo). */
export async function guardarDatosMl(lanzamientoProductoId: string, datos: DatosMlDeFicha): Promise<boolean> {
  // En fila por producto: el último pedido es el último que se escribe, y `fetchLanzamiento` espera los que
  // están en camino antes de releer (review Greptile #156).
  return guardadosDeLaFicha.encolar(lanzamientoProductoId, async () => {
    const { error } = await supabase
      .from("lanzamientos_productos")
      .update({ mercadolibre: datos, updated_at: new Date().toISOString() })
      .eq("id", lanzamientoProductoId);
    if (error) {
      console.error("[lanzamientosApi] datos de Mercado Libre", error);
      return false;
    }
    return true;
  });
}

const guardadosDeLaFicha = crearFilaDeGuardados();

/**
 * Cambia las tiendas de un lanzamiento (sumar o sacar Mercado Libre, por ejemplo). El `canal` pasa a
 * ser la primera. Devuelve false si no se pudo (sin la migración aplicada, cambiar a varias falla).
 */
export async function actualizarDestinos(lanzamientoId: string, destinos: CanalLanzamiento[]): Promise<boolean> {
  if (destinos.length === 0) return false;
  const { error } = await supabase
    .from("lanzamientos")
    .update({ canal: destinos[0], destinos: destinos.length > 1 ? destinos : null, updated_at: new Date().toISOString() })
    .eq("id", lanzamientoId);
  if (error) {
    console.error("[lanzamientosApi] destinos", error);
    return false;
  }
  return true;
}

export async function fetchLanzamientos(
  companyId: string,
  brandId: string | null,
  opciones: { conPortadas?: boolean } = {},
): Promise<Lanzamiento[]> {
  const { data, error } = await supabase
    .from("lanzamientos")
    .select(
      "id, nombre, canal, estado, fecha_lanzamiento, brand_id, created_at, " +
        "lanzamientos_productos(id, orden, estado, lanzamientos_imagenes(id, orden, file_name, imagenes_generadas(file_name)))",
    )
    .eq("id_company", companyId)
    // Los de la TIENDA: el calendario de Instagram vive en su sección y se lee con
    // `calendarioDeLaMarca` (ADR-2026-10-06-instagram-tiene-su-propia-seccion). Todo el que
    // llama acá —el listado, "Enviar a lanzamiento", el canvas, el chat— busca dónde
    // mandar fotos o qué publicar en una tienda, y un calendario no es ninguna de las dos.
    .neq("canal", "redes")
    .order("created_at", { ascending: false });
  if (error) {
    console.error("[lanzamientosApi] listado", error);
    return [];
  }
  const destinos = await destinosGuardados((data || []).map((r: any) => r.id as string));
  return Promise.all(
    (data || [])
      .filter((r: any) => !brandId || r.brand_id === null || r.brand_id === brandId)
      .map(async (r: any) => {
        const prods = ((r.lanzamientos_productos || []) as any[])
          .slice()
          .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
        return {
          id: r.id,
          nombre: r.nombre,
          canal: r.canal,
          destinos: destinosDe({ canal: r.canal, destinos: destinos.get(r.id) }),
          estado: r.estado,
          fechaLanzamiento: r.fecha_lanzamiento,
          brandId: r.brand_id,
          createdAt: r.created_at,
          productos: prods.length,
          imagenes: prods.reduce((n, p) => n + (p.lanzamientos_imagenes?.length ?? 0), 0),
          publicados: prods.filter((p) => p.estado === "publicado").length,
          conError: prods.filter((p) => p.estado === "error").length,
          portadas: opciones.conPortadas ? await portadasDe(companyId, prods) : [],
        };
      }),
  );
}

/**
 * Si la lista de Tienda online tendría al menos un lanzamiento, con el MISMO filtro que
 * `fetchLanzamientos` (empresa, sin calendarios de redes, la marca o ninguna) pero sin
 * traer productos ni fotos. `null` si la consulta falla: el que llama decide qué hacer
 * sin saber. Lo usa "Publicar en tu tienda" de Creaciones para elegir entre la lista y
 * el modal de crear (ADR-2026-10-16-publicar-en-tu-tienda-va-a-la-lista).
 */
export async function tieneLanzamientos(companyId: string, brandId: string | null): Promise<boolean | null> {
  let consulta = supabase
    .from("lanzamientos")
    .select("id")
    .eq("id_company", companyId)
    .neq("canal", "redes");
  if (brandId) consulta = consulta.or(`brand_id.eq.${brandId},brand_id.is.null`);
  const { data, error } = await consulta.limit(1);
  if (error) {
    console.error("[lanzamientosApi] tieneLanzamientos", error);
    return null;
  }
  return (data?.length ?? 0) > 0;
}

/** La primera foto de cada uno de los primeros tres productos que tengan alguna, firmada. */
async function portadasDe(companyId: string, prods: any[]): Promise<string[]> {
  const nombres = prods
    .map((p) => {
      const primera = ((p.lanzamientos_imagenes || []) as any[])
        .slice()
        .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))[0];
      if (!primera) return "";
      const ig = Array.isArray(primera.imagenes_generadas) ? primera.imagenes_generadas[0] : primera.imagenes_generadas;
      return (primera.file_name as string) || (ig?.file_name as string) || "";
    })
    .filter(Boolean)
    .slice(0, 3);
  const urls = await Promise.all(nombres.map((n) => firmarThumb(companyId, n)));
  return urls.filter(Boolean);
}

export async function crearLanzamiento(p: {
  companyId: string;
  brandId: string | null;
  nombre: string;
  canal: CanalLanzamiento;
  /** Varias tiendas a la vez (Tiendanube + Mercado Libre). Con una sola, alcanza `canal`. */
  destinos?: CanalLanzamiento[];
  userId?: string | null;
}): Promise<Lanzamiento | null> {
  const varias = (p.destinos?.length ?? 0) > 1 ? p.destinos! : null;
  const { data, error } = await supabase
    .from("lanzamientos")
    .insert({
      id_company: p.companyId,
      brand_id: p.brandId,
      nombre: p.nombre.trim() || "Lanzamiento",
      canal: varias ? varias[0] : p.canal,
      // Sólo si son varias: así crear un lanzamiento de una tienda no depende de la migración nueva.
      ...(varias ? { destinos: varias } : {}),
      created_by: p.userId ?? null,
    })
    .select("id, nombre, canal, estado, fecha_lanzamiento, brand_id, created_at")
    .single();
  if (error || !data) {
    console.error("[lanzamientosApi] crear", error);
    return null;
  }
  return {
    id: data.id,
    nombre: data.nombre,
    canal: data.canal,
    destinos: destinosDe({ canal: data.canal, destinos: varias }),
    estado: data.estado,
    fechaLanzamiento: data.fecha_lanzamiento,
    brandId: data.brand_id,
    createdAt: data.created_at,
    productos: 0,
    imagenes: 0,
    publicados: 0,
    conError: 0,
    portadas: [],
  };
}

export async function renombrarLanzamiento(id: string, nombre: string): Promise<void> {
  await supabase
    .from("lanzamientos")
    .update({ nombre: nombre.trim() || "Lanzamiento", updated_at: new Date().toISOString() })
    .eq("id", id);
}

export async function borrarLanzamiento(id: string): Promise<void> {
  await supabase.from("lanzamientos").delete().eq("id", id);
}

// ── Detalle ────────────────────────────────────────────────────────────────

export async function fetchLanzamiento(
  id: string,
  companyId: string,
): Promise<LanzamientoDetalle | null> {
  // Lo que la ficha está guardando en esta pestaña entra en la relectura: "Publicar" y el chat leen la base, y
  // un guardado en camino quedaba afuera (review Greptile #156).
  await guardadosDeLaFicha.esperarTodo();
  const { data, error } = await supabase
    .from("lanzamientos")
    .select(
      "id, nombre, canal, estado, fecha_lanzamiento, brand_id, created_at, " +
        "lanzamientos_productos(id, producto_id, orden, nombre, descripcion, precio, stock, sku, " +
        "accion, estado, error, costo, codigo_barras, peso, alto, ancho, " +
        "profundidad, marca, tags, seo_titulo, seo_descripcion, atributos, " +
        "precio_por_variante, envio_por_variante, imagenes_por_variante, " +
        "productos(nombre, tn_product_id), " +
        "lanzamientos_variantes(id, orden, valores, precio, costo, stock, " +
        "sku, codigo_barras, peso, alto, ancho, profundidad), " +
        "lanzamientos_productos_categorias(categoria_id), " +
        "lanzamientos_imagenes(id, imagen_generada_id, variante_id, lanzamiento_variante_id, orden, file_name, " +
        "imagenes_generadas(file_name)))",
    )
    .eq("id", id)
    .eq("id_company", companyId)
    .maybeSingle();
  if (error || !data) {
    if (error) console.error("[lanzamientosApi] detalle", error);
    return null;
  }

  const crudos = ((data as any).lanzamientos_productos || []) as any[];
  const productos: ProductoDeLanzamiento[] = await Promise.all(
    crudos
      .slice()
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
      .map(async (p) => {
        const prod = Array.isArray(p.productos) ? p.productos[0] : p.productos;
        const imgs = ((p.lanzamientos_imagenes || []) as any[])
          .slice()
          .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
        const conThumb = await Promise.all(
          imgs.map(async (i) => {
            const ig = Array.isArray(i.imagenes_generadas) ? i.imagenes_generadas[0] : i.imagenes_generadas;
            // Generada: el nombre sale de su fila. Subida: está en la fila del
            // lanzamiento. Las dos viven en el mismo lugar del storage.
            const fileName = (i.file_name as string) || (ig?.file_name as string) || "";
            return {
              id: i.id,
              imagenGeneradaId: i.imagen_generada_id ?? null,
              fileName,
              varianteId: i.variante_id,
              lanzamientoVarianteId: i.lanzamiento_variante_id ?? null,
              orden: i.orden ?? 0,
              thumbUrl: fileName ? await firmarThumb(companyId, fileName) : "",
              subida: i.imagen_generada_id == null,
            };
          }),
        );
        return {
          id: p.id,
          productoId: p.producto_id ?? null,
          orden: p.orden ?? 0,
          nombre: p.nombre || prod?.nombre || "Producto",
          descripcion: p.descripcion,
          precio: p.precio != null ? String(p.precio) : null,
          stock: p.stock,
          sku: p.sku,
          accion: p.accion,
          estado: p.estado,
          error: p.error,
          tnProductId: prod?.tn_product_id != null ? Number(prod.tn_product_id) : null,
          imagenes: conThumb,
          costo: aTexto(p.costo),
          codigoBarras: p.codigo_barras ?? null,
          peso: aTexto(p.peso),
          alto: aTexto(p.alto),
          ancho: aTexto(p.ancho),
          profundidad: aTexto(p.profundidad),
          marca: p.marca ?? null,
          tags: p.tags ?? null,
          seoTitulo: p.seo_titulo ?? null,
          seoDescripcion: p.seo_descripcion ?? null,
          atributos: Array.isArray(p.atributos) ? (p.atributos as string[]) : [],
          precioPorVariante: p.precio_por_variante === true,
          envioPorVariante: p.envio_por_variante === true,
          imagenesPorVariante: p.imagenes_por_variante === true,
          categorias: ((p.lanzamientos_productos_categorias || []) as Array<{ categoria_id: string }>)
            .map((c) => c.categoria_id),
          variantes: ((p.lanzamientos_variantes || []) as any[])
            .slice()
            .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
            .map((v) => ({
              id: v.id,
              orden: v.orden ?? 0,
              valores: Array.isArray(v.valores) ? (v.valores as string[]) : [],
              precio: aTexto(v.precio),
              costo: aTexto(v.costo),
              stock: v.stock != null ? String(v.stock) : null,
              sku: v.sku ?? null,
              codigoBarras: v.codigo_barras ?? null,
              peso: aTexto(v.peso),
              alto: aTexto(v.alto),
              ancho: aTexto(v.ancho),
              profundidad: aTexto(v.profundidad),
            })),
        };
      }),
  );

  const d = data as any;
  const [destinos, extras] = await Promise.all([destinosGuardados([d.id]), extrasDeProductos(d.id)]);
  for (const p of productos) {
    p.mercadolibre = extras.ml.get(p.id) ?? null;
    p.porDestino = extras.porDestino.get(p.id) ?? {};
  }
  return {
    id: d.id,
    nombre: d.nombre,
    canal: d.canal,
    destinos: destinosDe({ canal: d.canal, destinos: destinos.get(d.id) }),
    estado: d.estado,
    fechaLanzamiento: d.fecha_lanzamiento,
    brandId: d.brand_id,
    createdAt: d.created_at,
    productos: productos.length,
    imagenes: productos.reduce((n, p) => n + p.imagenes.length, 0),
    publicados: productos.filter((p) => p.estado === "publicado").length,
    conError: productos.filter((p) => p.estado === "error").length,
    portadas: productos.map((p) => p.imagenes[0]?.thumbUrl ?? "").filter(Boolean).slice(0, 3),
    productos_: productos,
  };
}

// ── Resolver el producto de una imagen ─────────────────────────────────────

export interface ProductoResuelto {
  productoId: string;
  varianteId: string;
  nombre: string;
  imagenUrl: string | null;
}

/**
 * De ids de VARIANTE a los PRODUCTOS a los que pertenecen. La UI de Atlas llama
 * "producto" a la variante, pero el lanzamiento agrupa por producto real: dos
 * variantes del mismo producto son un solo destino.
 */
/**
 * De id de VARIANTE a id de PRODUCTO, para TODAS las variantes pedidas.
 *
 * `productosDeVariantes` NO sirve para esto y es una trampa fácil: su contrato es "un
 * destino por producto", así que descarta las variantes hermanas
 * (`if (porProducto.has(p.id)) continue`). Con tres o más variantes del mismo producto, la
 * tercera quedaba sin mapear y terminaba en el selector manual aunque su producto se
 * supiera (review Greptile #122). La query es la misma y ya trae el dato: lo único que
 * cambia es que acá no se tira.
 *
 * Errores adentro, como su vecina: quien mapea decide qué hacer con lo que no resolvió.
 */
export async function productoPorVariante(varianteIds: string[]): Promise<Map<string, string>> {
  const mapa = new Map<string, string>();
  const unicos = [...new Set(varianteIds)];
  if (unicos.length === 0) return mapa;
  try {
    const filas = await fetchInChunks<any>(unicos, (chunk) =>
      supabase.from("variantes").select("id, product_id").in("id", chunk),
    );
    for (const v of filas) {
      if (v?.id && v?.product_id) mapa.set(String(v.id), String(v.product_id));
    }
  } catch (e) {
    console.error("[lanzamientosApi] productoPorVariante", e);
  }
  return mapa;
}

export async function productosDeVariantes(varianteIds: string[]): Promise<ProductoResuelto[]> {
  if (varianteIds.length === 0) return [];
  let filas: any[] = [];
  try {
    filas = await fetchInChunks<any>(varianteIds, (chunk) =>
      supabase
        .from("variantes")
        .select("id, nombre, product_id, productos(id, nombre)")
        .in("id", chunk),
    );
  } catch (e) {
    console.error("[lanzamientosApi] productosDeVariantes", e);
    return [];
  }
  const porProducto = new Map<string, ProductoResuelto>();
  for (const v of filas) {
    const p = Array.isArray(v.productos) ? v.productos[0] : v.productos;
    if (!p?.id) continue;
    if (porProducto.has(p.id)) continue;
    porProducto.set(p.id, {
      productoId: p.id,
      varianteId: v.id,
      nombre: p.nombre || v.nombre || "Producto",
      imagenUrl: null,
    });
  }
  // Orden de entrada: el principal de la generación queda primero.
  const orden = new Map(varianteIds.map((id, i) => [id, i]));
  return [...porProducto.values()].sort(
    (a, b) => (orden.get(a.varianteId) ?? 0) - (orden.get(b.varianteId) ?? 0),
  );
}

// ── Mandar imágenes a un lanzamiento ───────────────────────────────────────

export interface ItemAEnviar {
  imagenGeneradaId: string;
  productoId: string;
  varianteId: string | null;
}

export interface ResultadoEnvio {
  agregadas: number;
  yaEstaban: number;
  /** Cuántas filas borrador (cargadas por Excel) adoptó este envío, atándolas por SKU. */
  adoptadas: number;
  error: string | null;
}

/**
 * La ficha con la que nace un producto del lanzamiento cuando viene del CATÁLOGO: nombre y
 * precio del producto, descripción y SKU de su variante principal.
 *
 * Vive en una función porque la usan dos caminos —mandar imágenes y agregar el producto
 * suelto— y la regla de qué precio gana (`precio_web` antes que `precio_local`) o cuándo la
 * acción es "crear" en vez de "agregar fotos" no puede desincronizarse entre los dos.
 */
const COLUMNAS_DE_LA_FICHA = "id, nombre, precio_web, precio_local, tn_product_id, medidas, variantes(sku, descripcion)";

/**
 * Los productos del catálogo con lo que necesita su ficha inicial. `medidas_envio` es de la migración
 * 20261018120000: sin ella la consulta falla con 42703 y se repite sin esa columna (el paquete queda
 * vacío, como antes) — ADR-2026-10-18-la-importacion-de-tiendanube-trae-los-datos.
 */
async function leerCatalogoParaLaFicha(ids: string[], companyId?: string) {
  const leer = (columnas: string) => {
    const q = supabase.from("productos").select(columnas).in("id", ids);
    return companyId ? q.eq("id_company", companyId) : q;
  };
  const conEnvio = await leer(`${COLUMNAS_DE_LA_FICHA}, medidas_envio`);
  if (conEnvio.error?.code !== "42703") return conEnvio;
  return leer(COLUMNAS_DE_LA_FICHA);
}

function fichaInicialDesdeCatalogo(
  lanzamientoId: string,
  companyId: string,
  catalogo: unknown,
  productoId: string,
  orden: number,
): Record<string, unknown> {
  const c = ((catalogo || []) as any[]).find((r) => r.id === productoId);
  const v = c && (Array.isArray(c.variantes) ? c.variantes[0] : c.variantes);
  return {
    lanzamiento_id: lanzamientoId,
    id_company: companyId,
    producto_id: productoId,
    orden,
    nombre: c?.nombre ?? null,
    descripcion: v?.descripcion ?? null,
    precio: c?.precio_web ?? c?.precio_local ?? null,
    sku: v?.sku ?? null,
    // El paquete para el envío: el que trajo la tienda o, si se entrega armado, el del producto.
    ...paqueteInicialDesdeCatalogo(c?.medidas, c?.medidas_envio),
    // Un producto que ya existe en la tienda sólo recibe fotos; uno de Atlas hay que
    // crearlo allá.
    accion: c?.tn_product_id != null ? "add" : "create",
  };
}

/**
 * Suma imágenes a un lanzamiento. Crea el `lanzamientos_productos` si el producto
 * todavía no estaba, copiando la ficha del catálogo como punto de partida.
 * Idempotente: mandar dos veces la misma imagen al mismo producto no la duplica
 * (la restricción única de la tabla), y se reporta como "ya estaba".
 */
export async function enviarImagenesALanzamiento(
  lanzamientoId: string,
  companyId: string,
  items: ItemAEnviar[],
): Promise<ResultadoEnvio> {
  if (items.length === 0) return { agregadas: 0, yaEstaban: 0, adoptadas: 0, error: null };

  const productoIds = [...new Set(items.map((i) => i.productoId))];

  // Ficha inicial: nombre y precio del producto, descripción y SKU de su variante
  // principal. Si el producto ya vino de Tiendanube la acción por defecto es
  // agregar fotos; si es de Atlas, crearlo allá.
  const { data: catalogo, error: eCat } = await leerCatalogoParaLaFicha(productoIds);
  if (eCat) {
    console.error("[lanzamientosApi] catálogo", eCat);
    return { agregadas: 0, yaEstaban: 0, adoptadas: 0, error: "No se pudo leer el producto" };
  }

  const { data: existentes } = await supabase
    .from("lanzamientos_productos")
    .select("id, producto_id")
    .eq("lanzamiento_id", lanzamientoId)
    .in("producto_id", productoIds);

  const porProducto = new Map<string, string>(
    ((existentes || []) as any[]).map((r) => [r.producto_id as string, r.id as string]),
  );

  // ── Adopción por SKU (ADR-2026-09-09-productos-de-lanzamiento-sin-catalogo) ──
  // Antes de crear filas nuevas, ver si alguna fila HUÉRFANA del lanzamiento —cargada
  // por Excel, sin producto detrás— está esperando justamente a este producto. Si el SKU
  // coincide, se la adopta: la ficha que el usuario cargó queda intacta y encima le caen
  // las fotos. Sin esto, el mismo producto quedaría dos veces en el lanzamiento: una con
  // los datos y otra con las fotos.
  const sinAdoptar = productoIds.filter((id) => !porProducto.has(id));
  const adoptadas: string[] = [];
  if (sinAdoptar.length > 0) {
    const skuDeProducto = new Map<string, string>();
    for (const id of sinAdoptar) {
      const c = ((catalogo || []) as any[]).find((r) => r.id === id);
      const v = c && (Array.isArray(c.variantes) ? c.variantes[0] : c.variantes);
      const sku = String(v?.sku ?? "").trim();
      if (sku) skuDeProducto.set(id, sku);
    }
    if (skuDeProducto.size > 0) {
      const { data: huerfanas } = await supabase
        .from("lanzamientos_productos")
        .select("id, sku")
        .eq("lanzamiento_id", lanzamientoId)
        .is("producto_id", null);
      // Un SKU que aparece en DOS filas huérfanas no se adivina: adoptar la equivocada
      // mezcla los datos de dos productos y no hay forma de darse cuenta después.
      const porSku = new Map<string, string[]>();
      for (const h of ((huerfanas || []) as any[])) {
        const sku = String(h.sku ?? "").trim().toLowerCase();
        if (!sku) continue;
        porSku.set(sku, [...(porSku.get(sku) ?? []), h.id as string]);
      }
      for (const [productoId, sku] of skuDeProducto) {
        const candidatas = porSku.get(sku.toLowerCase()) ?? [];
        if (candidatas.length !== 1) continue;
        const filaId = candidatas[0]!;
        const { error: eAdop } = await supabase
          .from("lanzamientos_productos")
          .update({ producto_id: productoId, updated_at: new Date().toISOString() })
          .eq("id", filaId)
          .is("producto_id", null);
        if (eAdop) {
          console.error("[lanzamientosApi] adopción por sku", eAdop);
          continue;
        }
        porProducto.set(productoId, filaId);
        adoptadas.push(filaId);
        // Que no la adopte otro producto del mismo envío.
        porSku.set(sku.toLowerCase(), []);
      }
    }
  }

  const faltantes = productoIds.filter((id) => !porProducto.has(id));
  if (faltantes.length > 0) {
    const { count } = await supabase
      .from("lanzamientos_productos")
      .select("id", { count: "exact", head: true })
      .eq("lanzamiento_id", lanzamientoId);
    const yaHay = count ?? 0;
    if (yaHay + faltantes.length > MAX_PRODUCTOS_POR_LANZAMIENTO) {
      return {
        agregadas: 0,
        yaEstaban: 0,
        adoptadas: adoptadas.length,
        error: `Un lanzamiento admite hasta ${MAX_PRODUCTOS_POR_LANZAMIENTO} productos`,
      };
    }
    const nuevos = faltantes.map((id, i) =>
      fichaInicialDesdeCatalogo(lanzamientoId, companyId, catalogo, id, yaHay + i),
    );
    const { data: creados, error: eIns } = await supabase
      .from("lanzamientos_productos")
      .insert(nuevos)
      .select("id, producto_id");
    if (eIns) {
      console.error("[lanzamientosApi] alta de producto", eIns);
      return { agregadas: 0, yaEstaban: 0, adoptadas: adoptadas.length, error: "No se pudo agregar el producto al lanzamiento" };
    }
    for (const r of (creados || []) as any[]) porProducto.set(r.producto_id, r.id);
  }

  // Orden de cada imagen: después de las que ya tiene ese producto.
  const idsProducto = [...porProducto.values()];
  const { data: previas } = await supabase
    .from("lanzamientos_imagenes")
    .select("lanzamiento_producto_id, orden")
    .in("lanzamiento_producto_id", idsProducto);
  const siguiente = new Map<string, number>();
  for (const r of ((previas || []) as any[])) {
    const actual = siguiente.get(r.lanzamiento_producto_id) ?? 0;
    siguiente.set(r.lanzamiento_producto_id, Math.max(actual, (r.orden ?? 0) + 1));
  }

  const aInsertar: any[] = [];
  for (const it of items) {
    const lpId = porProducto.get(it.productoId);
    if (!lpId) continue;
    const orden = siguiente.get(lpId) ?? 0;
    if (orden >= MAX_IMAGENES_POR_PRODUCTO) continue;
    siguiente.set(lpId, orden + 1);
    aInsertar.push({
      lanzamiento_producto_id: lpId,
      id_company: companyId,
      imagen_generada_id: it.imagenGeneradaId,
      variante_id: it.varianteId,
      orden,
    });
  }
  if (aInsertar.length === 0) {
    return {
      agregadas: 0,
      yaEstaban: 0,
      adoptadas: adoptadas.length,
      error: `Ese producto ya tiene el máximo de ${MAX_IMAGENES_POR_PRODUCTO} fotos`,
    };
  }

  // `ignoreDuplicates` sobre la restricción única: reenviar la misma imagen no
  // duplica ni rompe, simplemente no suma.
  const { data: insertadas, error: eImg } = await supabase
    .from("lanzamientos_imagenes")
    .upsert(aInsertar, {
      onConflict: "lanzamiento_producto_id,imagen_generada_id",
      ignoreDuplicates: true,
    })
    .select("id");
  if (eImg) {
    console.error("[lanzamientosApi] alta de imagen", eImg);
    return { agregadas: 0, yaEstaban: 0, adoptadas: adoptadas.length, error: "No se pudo agregar la imagen" };
  }
  const agregadas = (insertadas || []).length;
  await supabase
    .from("lanzamientos")
    .update({ updated_at: new Date().toISOString() })
    .eq("id", lanzamientoId);
  return {
    agregadas,
    yaEstaban: aInsertar.length - agregadas,
    adoptadas: adoptadas.length,
    error: null,
  };
}

// ── Edición del detalle ────────────────────────────────────────────────────

/** Campos de la ficha que la pantalla puede guardar. */
export type CamposDeFicha = Partial<
  Pick<
    ProductoDeLanzamiento,
    | "nombre"
    | "descripcion"
    | "precio"
    | "stock"
    | "sku"
    | "accion"
    | "costo"
    | "codigoBarras"
    | "peso"
    | "alto"
    | "ancho"
    | "profundidad"
    | "marca"
    | "tags"
    | "seoTitulo"
    | "seoDescripcion"
    | "atributos"
    | "precioPorVariante"
    | "envioPorVariante"
    | "imagenesPorVariante"
  >
>;

/** Texto del formulario → `numeric` de Postgres. Vacío es NULL, no cero: "sin
 *  dato" y "cero" son cosas distintas para un costo o un peso. */
function aNumerico(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const t = String(v).trim().replace(",", ".");
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

const COLUMNA_DE_CAMPO: Record<string, string> = {
  nombre: "nombre",
  descripcion: "descripcion",
  sku: "sku",
  accion: "accion",
  codigoBarras: "codigo_barras",
  marca: "marca",
  tags: "tags",
  seoTitulo: "seo_titulo",
  seoDescripcion: "seo_descripcion",
};

const CAMPO_NUMERICO: Record<string, string> = {
  precio: "precio",
  costo: "costo",
  peso: "peso",
  alto: "alto",
  ancho: "ancho",
  profundidad: "profundidad",
};

export async function actualizarFicha(
  lanzamientoProductoId: string,
  campos: CamposDeFicha,
): Promise<{ error: string | null }> {
  const patch: Record<string, unknown> = { updated_at: new Date().toISOString() };
  for (const [campo, valor] of Object.entries(campos)) {
    if (valor === undefined) continue;
    const columnaTexto = COLUMNA_DE_CAMPO[campo];
    if (columnaTexto) {
      patch[columnaTexto] = valor === "" ? null : valor;
      continue;
    }
    const columnaNumero = CAMPO_NUMERICO[campo];
    if (columnaNumero) {
      patch[columnaNumero] = aNumerico(valor);
      continue;
    }
    if (campo === "stock") patch.stock = aNumerico(valor);
    if (campo === "atributos") patch.atributos = valor;
    if (campo === "precioPorVariante") patch.precio_por_variante = valor === true;
    if (campo === "envioPorVariante") patch.envio_por_variante = valor === true;
    if (campo === "imagenesPorVariante") patch.imagenes_por_variante = valor === true;
  }
  const { error } = await supabase.from("lanzamientos_productos").update(patch).eq("id", lanzamientoProductoId);
  if (error) console.error("[lanzamientosApi] ficha", error);
  return { error: error ? "No se pudo guardar la ficha" : null };
}

// ── Variantes ──────────────────────────────────────────────────────────────

/** Devuelve el id de la variante nueva: con las fotos por variante prendidas, la
 *  primera que se crea se lleva las que todavía son del producto. */
export async function agregarVariante(
  lanzamientoProductoId: string,
  companyId: string,
  valores: string[],
  orden: number,
): Promise<string | null> {
  const { data, error } = await supabase
    .from("lanzamientos_variantes")
    .insert({
      lanzamiento_producto_id: lanzamientoProductoId,
      id_company: companyId,
      valores,
      orden,
    })
    .select("id")
    .single();
  if (error) {
    console.error("[lanzamientosApi] agregar variante", error);
    return null;
  }
  return (data as { id: string } | null)?.id ?? null;
}

/**
 * Reordena las variantes de un producto.
 *
 * El orden importa de verdad: `tnBodyDeProducto` las manda ordenadas por `orden`
 * y Tiendanube las crea en ese orden, que es el que ve el comprador en el
 * selector de la tienda. Reordenarlas acá es reordenar lo que se va a publicar.
 *
 * Escribe 0..n-1 en vez de intercambiar dos: así de paso normaliza los `orden`
 * repetidos o con huecos que pueda haber dejado un alta concurrente. Devuelve el
 * error porque la pantalla mueve la variante ANTES de guardar.
 */
export async function reordenarVariantes(idsEnOrden: string[]): Promise<{ error: string | null }> {
  const ahora = new Date().toISOString();
  const res = await Promise.all(
    idsEnOrden.map((id, i) =>
      supabase.from("lanzamientos_variantes").update({ orden: i, updated_at: ahora }).eq("id", id),
    ),
  );
  const falla = res.find((r) => r.error);
  if (falla?.error) {
    console.error("[lanzamientosApi] reordenar variantes", falla.error);
    return { error: "No se pudo guardar el orden de las variantes" };
  }
  return { error: null };
}

export type CamposDeVariante = Partial<Omit<VarianteDeLanzamiento, "id">>;

export async function actualizarVariante(id: string, campos: CamposDeVariante): Promise<{ error: string | null }> {
  const patch: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (campos.valores !== undefined) patch.valores = campos.valores;
  if (campos.orden !== undefined) patch.orden = campos.orden;
  if (campos.sku !== undefined) patch.sku = campos.sku === "" ? null : campos.sku;
  if (campos.codigoBarras !== undefined)
    patch.codigo_barras = campos.codigoBarras === "" ? null : campos.codigoBarras;
  if (campos.stock !== undefined) patch.stock = aNumerico(campos.stock);
  for (const [campo, columna] of Object.entries({
    precio: "precio",
    costo: "costo",
    peso: "peso",
    alto: "alto",
    ancho: "ancho",
    profundidad: "profundidad",
  })) {
    const v = (campos as Record<string, unknown>)[campo];
    if (v !== undefined) patch[columna] = aNumerico(v);
  }
  const { error } = await supabase.from("lanzamientos_variantes").update(patch).eq("id", id);
  if (error) console.error("[lanzamientosApi] variante", error);
  return { error: error ? "No se pudo guardar la variante" : null };
}

export async function quitarVariante(id: string): Promise<{ error: string | null }> {
  const { error } = await supabase.from("lanzamientos_variantes").delete().eq("id", id);
  if (error) console.error("[lanzamientosApi] quitar variante", error);
  return { error: error ? "No se pudo quitar la variante" : null };
}

export async function quitarImagen(id: string): Promise<{ error: string | null }> {
  const { error } = await supabase.from("lanzamientos_imagenes").delete().eq("id", id);
  if (error) {
    console.error("[lanzamientosApi] quitar imagen", error);
    return { error: "No se pudo quitar la foto" };
  }
  return { error: null };
}

export async function quitarProducto(id: string): Promise<void> {
  await supabase.from("lanzamientos_productos").delete().eq("id", id);
}

/** Asigna una foto a una variante (o la deja general con `null`). */
/**
 * Manda a una variante todas las fotos del producto que no son de ninguna.
 *
 * Es la red de seguridad de "las fotos se muestran por variante": una foto sin
 * variante no aparece en ninguna tira, pero se publica igual en la galería. Se
 * usa al prender la opción, al crear la primera variante y al borrar una —
 * borrar una variante deja sus fotos en NULL (el FK es ON DELETE SET NULL).
 *
 * Un solo UPDATE con el filtro puesto: no hace falta traer los ids primero, y
 * así no hay ventana entre leer y escribir.
 */
export async function asignarSueltasAVariante(
  lanzamientoProductoId: string,
  lanzamientoVarianteId: string,
): Promise<void> {
  const { error } = await supabase
    .from("lanzamientos_imagenes")
    .update({ lanzamiento_variante_id: lanzamientoVarianteId })
    .eq("lanzamiento_producto_id", lanzamientoProductoId)
    .is("lanzamiento_variante_id", null);
  if (error) console.error("[lanzamientosApi] asignar sueltas", error);
}

/**
 * Manda una foto a una variante (o la deja sin ninguna con `null`).
 *
 * Devuelve el error en vez de tragárselo: la pantalla mueve la foto ANTES de
 * guardar, y sin esto un fallo dejaría al usuario viendo la foto en una variante
 * que la base no tiene — y publicando con la asignación vieja sin enterarse. Es
 * la misma razón que en `reordenarImagenes`.
 */
export async function asignarImagenAVariante(
  imagenId: string,
  lanzamientoVarianteId: string | null,
): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from("lanzamientos_imagenes")
    .update({ lanzamiento_variante_id: lanzamientoVarianteId })
    .eq("id", imagenId);
  if (error) {
    console.error("[lanzamientosApi] asignar imagen", error);
    return { error: "No se pudo mover la foto" };
  }
  return { error: null };
}

/**
 * Reordena las fotos de un producto. El orden 0 es la portada en replace/create.
 *
 * Devuelve si falló porque la pantalla aplica el orden ANTES de guardar: sin esto,
 * un error dejaría al usuario viendo un orden que la base no tiene, y publicaría
 * en el viejo sin avisar.
 */
export async function reordenarImagenes(idsEnOrden: string[]): Promise<{ error: string | null }> {
  const res = await Promise.all(
    idsEnOrden.map((id, i) => supabase.from("lanzamientos_imagenes").update({ orden: i }).eq("id", id)),
  );
  const falla = res.find((r) => r.error);
  if (falla?.error) {
    console.error("[lanzamientosApi] reordenar", falla.error);
    return { error: "No se pudo guardar el orden de las fotos" };
  }
  return { error: null };
}

// ── Comentarios sobre un producto ──────────────────────────────────────────
// ADR-2026-09-08-comentarios-en-el-producto. Son del PRODUCTO del lanzamiento,
// no del catálogo: hablan de este borrador y se van con él.

export interface ComentarioDeProducto {
  id: string;
  texto: string;
  resuelto: boolean;
  createdAt: string;
  autorId: string;
  /** Nombre para mostrar. Cae al email y después a "Alguien" si no hay perfil. */
  autor: string;
  /** Si lo escribió quien está mirando: decide si puede borrarlo. */
  mio: boolean;
  /** Comentario dejado por un visitante externo (calendario compartido). */
  esVisitante?: boolean;
}

export async function fetchComentarios(
  lanzamientoProductoId: string,
  miUserId: string | null,
): Promise<ComentarioDeProducto[]> {
  // Comentarios del equipo
  const { data, error } = await supabase
    .from("lanzamientos_comentarios")
    .select("id, texto, resuelto, created_at, autor_id, profiles(name, name_short, email)")
    .eq("lanzamiento_producto_id", lanzamientoProductoId)
    .order("created_at", { ascending: true });
  if (error) {
    console.error("[lanzamientosApi] comentarios", error);
    return [];
  }
  const delEquipo: ComentarioDeProducto[] = (data || []).map((c: any) => {
    const pf = Array.isArray(c.profiles) ? c.profiles[0] : c.profiles;
    return {
      id: c.id,
      texto: c.texto,
      resuelto: c.resuelto === true,
      createdAt: c.created_at,
      autorId: c.autor_id,
      autor: pf?.name_short || pf?.name || pf?.email || "Alguien",
      mio: !!miUserId && c.autor_id === miUserId,
    };
  });

  // Comentarios de visitante (calendario compartido)
  const { data: visitantes } = await supabase
    .from("comentarios_de_visitante")
    .select("id, texto, resuelto, created_at, nombre_visitante")
    .eq("lanzamiento_producto_id", lanzamientoProductoId)
    .order("created_at", { ascending: true });

  // Comentarios de visitante: se marcan con esVisitante para que
  // resolverComentario y borrarComentario operen sobre la tabla correcta.
  const deVisitantes: ComentarioDeProducto[] = (visitantes || []).map((c: any) => ({
    id: c.id,
    texto: c.texto,
    resuelto: c.resuelto === true,
    createdAt: c.created_at,
    autorId: "visitante",
    autor: c.nombre_visitante ?? "Visitante",
    mio: false, // los visitantes no se "borran a sí mismos" desde este panel
    esVisitante: true, // → resolver/borrar usan comentarios_de_visitante
  }));

  return [...delEquipo, ...deVisitantes].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
}

/**
 * Cuántos comentarios SIN RESOLVER tiene cada producto de una lista.
 *
 * Se pide aparte y no dentro del detalle del lanzamiento porque es lo único que
 * la tabla necesita saber —un número por producto— y traer los hilos enteros
 * para contar sería traer todo el texto de todos los productos en cada carga.
 */
export async function contarComentariosPendientes(
  lanzamientoProductoIds: string[],
): Promise<Map<string, number>> {
  const out = new Map<string, number>();
  if (lanzamientoProductoIds.length === 0) return out;
  // `.in()` con muchos ids desborda la URL y da 400 (project_supabase_in_url_limit).
  // Comentarios del equipo
  const filas = await fetchInChunks<{ lanzamiento_producto_id: string }>(
    lanzamientoProductoIds,
    (chunk) =>
      supabase
        .from("lanzamientos_comentarios")
        .select("lanzamiento_producto_id")
        .eq("resuelto", false)
        .in("lanzamiento_producto_id", chunk),
  );
  for (const f of filas) {
    out.set(f.lanzamiento_producto_id, (out.get(f.lanzamiento_producto_id) ?? 0) + 1);
  }
  // Comentarios de visitante
  const filasV = await fetchInChunks<{ lanzamiento_producto_id: string }>(
    lanzamientoProductoIds,
    (chunk) =>
      supabase
        .from("comentarios_de_visitante")
        .select("lanzamiento_producto_id")
        .eq("resuelto", false)
        .in("lanzamiento_producto_id", chunk),
  );
  for (const f of filasV) {
    out.set(f.lanzamiento_producto_id, (out.get(f.lanzamiento_producto_id) ?? 0) + 1);
  }
  return out;
}

export async function comentar(
  lanzamientoProductoId: string,
  companyId: string,
  userId: string,
  texto: string,
): Promise<{ error: string | null }> {
  const { error } = await supabase.from("lanzamientos_comentarios").insert({
    lanzamiento_producto_id: lanzamientoProductoId,
    id_company: companyId,
    // Va explícito además del DEFAULT porque la policy de INSERT lo exige: es lo
    // que impide firmar un comentario con el nombre de otro.
    autor_id: userId,
    texto: texto.trim(),
  });
  if (error) {
    console.error("[lanzamientosApi] comentar", error);
    return { error: "No se pudo dejar el comentario" };
  }
  return { error: null };
}

/** Tachar o destachar. Lo puede hacer cualquiera de la empresa: quien arregla lo
 *  señalado no suele ser quien lo señaló. */
export async function resolverComentario(
  id: string,
  resuelto: boolean,
  esVisitante?: boolean,
): Promise<{ error: string | null }> {
  const tabla = esVisitante ? "comentarios_de_visitante" : "lanzamientos_comentarios";
  const campos: Record<string, unknown> = { resuelto };
  if (!esVisitante) campos.updated_at = new Date().toISOString();
  const { error } = await supabase.from(tabla).update(campos).eq("id", id);
  if (error) {
    console.error("[lanzamientosApi] resolver comentario", error);
    return { error: "No se pudo marcar el comentario" };
  }
  return { error: null };
}

/** Solo el autor. La RLS lo garantiza; el front ni ofrece el botón en los ajenos. */
export async function borrarComentario(id: string, esVisitante?: boolean): Promise<{ error: string | null }> {
  const tabla = esVisitante ? "comentarios_de_visitante" : "lanzamientos_comentarios";
  const { error } = await supabase.from(tabla).delete().eq("id", id);
  if (error) {
    console.error("[lanzamientosApi] borrar comentario", error);
    return { error: "No se pudo borrar el comentario" };
  }
  return { error: null };
}

// ── Categorías de la tienda ────────────────────────────────────────────────
// ADR-2026-09-07-categorias-de-tienda. NO son del lanzamiento: son de la marca y
// se comparten entre todos sus lanzamientos. Lo que sí es del lanzamiento es a
// cuáles quedó atado cada producto.

export interface CategoriaDeTienda {
  id: string;
  nombre: string;
  parentId: string | null;
  /** Con qué categoría de Tiendanube quedó apareada. `null` = todavía no existe allá. */
  tnCategoryId: number | null;
}

/**
 * El árbol de la marca activa.
 *
 * Incluye las que no tienen marca, igual que `fetchLanzamientos`: las creadas
 * antes de que existiera una no tienen por qué desaparecer.
 */
export async function fetchCategorias(
  companyId: string,
  brandId: string | null,
): Promise<CategoriaDeTienda[]> {
  const { data, error } = await supabase
    .from("categorias_tienda")
    .select("id, nombre, parent_id, tn_category_id, brand_id")
    .eq("id_company", companyId);
  if (error) {
    console.error("[lanzamientosApi] categorías", error);
    return [];
  }
  return (data || [])
    .filter((c: any) => !brandId || c.brand_id === null || c.brand_id === brandId)
    .map((c: any) => ({
      id: c.id,
      nombre: c.nombre,
      parentId: c.parent_id ?? null,
      tnCategoryId: c.tn_category_id != null ? Number(c.tn_category_id) : null,
    }));
}

/** Un mensaje que se pueda leer, de los dos errores que la base sabe dar acá. */
function errorDeCategoria(e: { code?: string; message?: string }, alCrear: boolean): string {
  if (e.code === "23505") return "Ya tenés una categoría con ese nombre en el mismo lugar";
  if (e.message?.includes("subcategorías")) return "No podés meter una categoría adentro de una suya";
  return alCrear ? "No se pudo crear la categoría" : "No se pudo guardar la categoría";
}

export async function crearCategoria(
  companyId: string,
  brandId: string | null,
  nombre: string,
  parentId: string | null,
): Promise<{ id: string | null; error: string | null }> {
  const { data, error } = await supabase
    .from("categorias_tienda")
    .insert({ id_company: companyId, brand_id: brandId, nombre: nombre.trim(), parent_id: parentId })
    .select("id")
    .single();
  if (error) {
    console.error("[lanzamientosApi] crear categoría", error);
    return { id: null, error: errorDeCategoria(error, true) };
  }
  return { id: (data as { id: string }).id, error: null };
}

export async function renombrarCategoria(id: string, nombre: string): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from("categorias_tienda")
    .update({ nombre: nombre.trim(), updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) {
    console.error("[lanzamientosApi] renombrar categoría", error);
    return { error: errorDeCategoria(error, false) };
  }
  return { error: null };
}

/** Colgarla de otra madre, o de la raíz con `null`. */
export async function moverCategoria(
  id: string,
  parentId: string | null,
): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from("categorias_tienda")
    .update({ parent_id: parentId, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) {
    console.error("[lanzamientosApi] mover categoría", error);
    return { error: errorDeCategoria(error, false) };
  }
  return { error: null };
}

/** Se lleva su rama entera: el FK de la madre es ON DELETE CASCADE. */
export async function borrarCategoria(id: string): Promise<{ error: string | null }> {
  const { error } = await supabase.from("categorias_tienda").delete().eq("id", id);
  if (error) {
    console.error("[lanzamientosApi] borrar categoría", error);
    return { error: "No se pudo borrar la categoría" };
  }
  return { error: null };
}

/** Ata o desata un producto del lanzamiento a una categoría. */
export async function marcarCategoriaDeProducto(
  lanzamientoProductoId: string,
  categoriaId: string,
  companyId: string,
  puesta: boolean,
): Promise<{ error: string | null }> {
  const { error } = puesta
    ? await supabase.from("lanzamientos_productos_categorias").upsert(
        {
          lanzamiento_producto_id: lanzamientoProductoId,
          categoria_id: categoriaId,
          id_company: companyId,
        },
        { onConflict: "lanzamiento_producto_id,categoria_id", ignoreDuplicates: true },
      )
    : await supabase
        .from("lanzamientos_productos_categorias")
        .delete()
        .eq("lanzamiento_producto_id", lanzamientoProductoId)
        .eq("categoria_id", categoriaId);
  if (error) {
    console.error("[lanzamientosApi] categoría del producto", error);
    return { error: "No se pudo guardar la categoría del producto" };
  }
  return { error: null };
}

/**
 * Deja al producto con UNA SOLA categoría — los canales que no admiten varias (VTEX).
 *
 * Dos escrituras y no N+1: se pone la elegida y se sacan TODAS las demás en un solo
 * DELETE. Supabase no expone transacciones desde el navegador, así que esto NO es atómico:
 * si la segunda falla, la base queda con la nueva más las viejas. Por eso el caller no
 * puede repintar lo que había —sería mentir, y al recargar aparecería otra cosa—: relee.
 *
 * El orden importa: primero poner y después sacar. Al revés, un fallo en el medio deja al
 * producto sin ninguna categoría; así, en el peor caso queda con una de más, que es lo que
 * la validación de "listo para publicar" ya sabe señalar.
 */
export async function reemplazarCategoriaDeProducto(
  lanzamientoProductoId: string,
  categoriaId: string,
  companyId: string,
): Promise<{ error: string | null }> {
  const puesta = await marcarCategoriaDeProducto(lanzamientoProductoId, categoriaId, companyId, true);
  if (puesta.error) return puesta;
  const { error } = await supabase
    .from("lanzamientos_productos_categorias")
    .delete()
    .eq("lanzamiento_producto_id", lanzamientoProductoId)
    .eq("id_company", companyId)
    .neq("categoria_id", categoriaId);
  if (error) {
    console.error("[lanzamientosApi] reemplazar categoría del producto", error);
    return { error: "No se pudo guardar la categoría del producto" };
  }
  return { error: null };
}

// ── Publicar ───────────────────────────────────────────────────────────────

export interface ProblemaDePublicacion {
  productoId: string | null;
  nombre: string;
  motivo: string;
}

/**
 * Qué le falta al lanzamiento para poder publicarse. Un producto que se va a CREAR
 * en el destino necesita nombre y precio (el backend los exige); uno que ya existe
 * allá solo necesita al menos una foto.
 */
/**
 * El camino de una categoría, de la raíz a la hoja, con lo que cada nivel ya
 * tenga en Tiendanube.
 *
 * Se corta si aparece un ciclo o una madre que no está: el front recibe lo que
 * le mandan, y un camino infinito colgaría el armado del payload.
 */
function caminoDeCategoria(
  arbol: readonly CategoriaDeTienda[],
  id: string,
): NivelDeCategoria[] {
  const porId = new Map(arbol.map((c) => [c.id, c]));
  const camino: NivelDeCategoria[] = [];
  const vistas = new Set<string>();
  let actual = porId.get(id);
  while (actual && !vistas.has(actual.id)) {
    vistas.add(actual.id);
    camino.unshift({ id: actual.id, nombre: actual.nombre, tn_category_id: actual.tnCategoryId });
    actual = actual.parentId ? porId.get(actual.parentId) : undefined;
  }
  return camino;
}

/** La ficha de un producto del lanzamiento, en el vocabulario de `fichaTiendanube`. */
export function fichaDe(
  p: ProductoDeLanzamiento,
  /** El árbol de la marca. Sin él la ficha viaja sin categorías. */
  arbol: readonly CategoriaDeTienda[] = [],
): FichaProducto {
  return {
    nombre: p.nombre,
    descripcion: p.descripcion,
    precio: p.precio,
    costo: p.costo,
    stock: p.stock != null ? String(p.stock) : null,
    sku: p.sku,
    codigoBarras: p.codigoBarras,
    peso: p.peso,
    alto: p.alto,
    ancho: p.ancho,
    profundidad: p.profundidad,
    marca: p.marca,
    tags: p.tags,
    seoTitulo: p.seoTitulo,
    seoDescripcion: p.seoDescripcion,
    atributos: p.atributos,
    categorias: p.categorias
      .map((id) => caminoDeCategoria(arbol, id))
      .filter((camino) => camino.length > 0),
    variantes: p.variantes.map((v) => ({
      ...v,
      // La PRIMERA foto de la variante es la que Tiendanube muestra al elegirla:
      // el resto van a la galería del producto igual que todas. `p.imagenes` ya
      // viene ordenada por `orden`, así que `find` es esa primera.
      //
      // La etiqueta tiene que ser la MISMA que manda `payloadDeExport`
      // (`imagenGeneradaId ?? fileName`): el worker linkea la variante buscando
      // en el mapa de lo que subió, que está armado con esa clave. Con solo
      // `imagenGeneradaId`, una foto SUBIDA por el usuario —que no tiene fila de
      // generación— nunca llegaba a ser la foto de su variante.
      imagenGeneradaId: (() => {
        const suya = p.imagenes.find((i) => i.lanzamientoVarianteId === v.id);
        return suya ? (suya.imagenGeneradaId ?? suya.fileName) : null;
      })(),
    })),
    precioPorVariante: p.precioPorVariante,
    envioPorVariante: p.envioPorVariante,
    imagenesPorVariante: p.imagenesPorVariante,
  };
}

export function problemasParaPublicar(
  productos: ProductoDeLanzamiento[],
  /** El destino decide las reglas: la misma ficha puede estar lista para
   *  Tiendanube y no para VTEX (ADR-2026-09-13-lanzamientos-para-vtex). Con varias
   *  tiendas, gana la regla más exigente de cada una
   *  (ADR-2026-09-30-lanzamiento-a-varias-tiendas). */
  destino: CanalLanzamiento | readonly CanalLanzamiento[] = "tiendanube",
): ProblemaDePublicacion[] {
  const problemas: ProblemaDePublicacion[] = [];
  const destinos: readonly CanalLanzamiento[] = typeof destino === "string" ? [destino] : destino;
  const reglas = reglasDeDestinos(destinos);
  for (const p of productos) {
    // La fila huérfana no es un producto al que "le faltan fotos": es una ficha esperando
    // su producto. Decirle "no tiene fotos" mandaría al usuario a buscar una foto para
    // subir, cuando lo que tiene que hacer es mandar el producto con ese SKU
    // (ADR-2026-09-09-productos-de-lanzamiento-sin-catalogo).
    if (!p.productoId) {
      problemas.push({
        productoId: null,
        nombre: p.nombre,
        motivo: p.sku
          ? `todavía no está atado a un producto del catálogo (esperando el SKU ${p.sku})`
          : "todavía no está atado a un producto del catálogo, y no tiene SKU para atarlo",
      });
      continue;
    }
    if (p.imagenes.length === 0) {
      problemas.push({ productoId: p.productoId, nombre: p.nombre, motivo: "no tiene fotos" });
      continue;
    }
    // A un producto que ya existe en la tienda solo se le suman fotos: su ficha
    // vive allá y no hay nada que validar acá. `tn_product_id` es de Tiendanube:
    // para otro destino ese producto es nuevo y su ficha se valida entera.
    // Si además va a otra tienda (Mercado Libre), allá se CREA: la ficha se valida entera.
    if (destinos.length === 1 && destinos[0] === "tiendanube" && p.tnProductId != null) continue;
    const falta = problemasDeFicha(fichaDe(p), reglas);
    if (falta.length > 0) {
      problemas.push({ productoId: p.productoId, nombre: p.nombre, motivo: falta[0]!.mensaje });
    }
  }
  return problemas;
}

/**
 * Arma el payload del export a Tiendanube desde el lanzamiento. Es la traducción
 * entre el vocabulario del lanzamiento y `TnExportProducto`, y la parte que se
 * puede testear sin red.
 */
export function payloadDeExport(
  productos: ProductoDeLanzamiento[],
  urlPorImagen: Map<string, string>,
  /** El árbol de categorías de la marca. Sin él los productos se publican sin
   *  categoría, que es como venía siendo hasta ahora. */
  arbol: readonly CategoriaDeTienda[] = [],
): Array<{
  producto_id: string;
  action: AccionProducto;
  tn_product_id: number | null;
  nuevo?: TnProductoBody;
  lanzamiento_producto_id: string;
  imagenes: Array<{ imagen_generada_id?: string; archivo?: string; src: string; orden: number }>;
}> {
  return productos
    // Una fila borrador (sin producto del catálogo) NO se publica: el backend usa el
    // `producto_id` para linkear con Tiendanube y para que un reintento no duplique el
    // producto en la tienda. Se filtra ACÁ y explícito
    // (ADR-2026-09-09-productos-de-lanzamiento-sin-catalogo): hoy tampoco pasaría el
    // filtro de fotos de más abajo, pero apoyarse en eso sería frágil — el día que una
    // huérfana tenga una foto subida a mano, se publicaría sin link.
    .filter((p): p is ProductoDeLanzamiento & { productoId: string } => !!p.productoId)
    .map((p) => {
      const imagenes = p.imagenes
        .map((i, idx) => ({
          // Una foto GENERADA se identifica por su fila de generación; una SUBIDA a mano
          // no tiene, y va como `archivo` (antes viajaba en `imagen_generada_id` y el backend
          // rechazaba el pedido entero: ADR-2026-09-30-publicar-lanzamiento-a-tiendanube-bugs).
          // La URL se firma igual para las dos.
          ...(i.imagenGeneradaId ? { imagen_generada_id: i.imagenGeneradaId } : { archivo: i.fileName }),
          src: urlPorImagen.get(i.imagenGeneradaId ?? i.fileName) ?? "",
          orden: idx,
        }))
        .filter((i) => i.src);
      // Un producto que ya existe en el destino se actualiza; uno de Atlas se crea.
      const action: AccionProducto = p.tnProductId != null ? p.accion === "replace" ? "replace" : "add" : "create";
      return {
        producto_id: p.productoId,
        action,
        tn_product_id: p.tnProductId,
        // Su fila del lanzamiento: el worker anota ahí cómo le fue (se publique desde la
        // pantalla o desde el chat).
        lanzamiento_producto_id: p.id,
        // La ficha completa solo viaja al CREAR: a un producto que ya existe en la
        // tienda no se le pisan los datos, solo se le suman fotos.
        ...(action === "create" ? { nuevo: tnBodyDeProducto(fichaDe(p, arbol)) } : {}),
        imagenes,
      };
    })
    .filter((p) => p.imagenes.length > 0);
}

/**
 * Marca el lanzamiento y los productos QUE SE MANDAN como en curso. Sólo ésos: uno que no
 * viaja (sin fotos, o una fila borrador) quedaría "publicando" para siempre, porque el
 * worker nunca lo va a anotar como terminado.
 */
export async function marcarPublicando(lanzamientoId: string, lanzamientoProductoIds?: string[]): Promise<void> {
  const ahora = new Date().toISOString();
  await supabase
    .from("lanzamientos")
    .update({ estado: "publicando", updated_at: ahora })
    .eq("id", lanzamientoId);
  let q = supabase
    .from("lanzamientos_productos")
    .update({ estado: "publicando", error: null, updated_at: ahora })
    .eq("lanzamiento_id", lanzamientoId);
  if (lanzamientoProductoIds) q = q.in("id", lanzamientoProductoIds);
  await q;
}

/**
 * Cómo viene la publicación: lo anota el WORKER en `lanzamientos_productos.estado` y
 * `lanzamientos.estado` a medida que termina cada producto
 * (ADR-2026-09-30-publicar-lanzamiento-a-tiendanube-bugs, atlas-backend). Acá sólo se lee.
 *
 * Antes se adivinaba mirando `imagenes_generadas.tn_exported_at`: una foto subida a mano no
 * tiene esa fila (el producto nunca terminaba) y publicar desde el chat no lo miraba nadie
 * ("Publicando" para siempre). `subidas`/`total` cuentan PRODUCTOS terminados de los que
 * están en esta publicación.
 */
export async function revisarPublicacion(
  lanzamientoId: string,
  _companyId: string,
): Promise<{ subidas: number; total: number; terminado: boolean; conErrores: boolean; motivo: string | null }> {
  const { data, error } = await supabase
    .from("lanzamientos_productos")
    .select("id, estado, nombre, error")
    .eq("lanzamiento_id", lanzamientoId)
    .order("orden", { ascending: true });
  if (error) {
    console.error("[lanzamientosApi] revisarPublicacion", error);
    return { subidas: 0, total: 0, terminado: false, conErrores: false, motivo: null };
  }
  return resumenDePublicacion((data || []) as FilaDePublicacion[]);
}

type FilaDePublicacion = { id: string; estado: string; nombre: string | null; error: string | null };

/**
 * Cómo va la publicación, y el motivo del primer producto que falló, para que el aviso final lo diga sin
 * tener que abrir cada uno (prueba del 2/10: decía "algunos no se pudieron publicar" y nada más).
 */
export function resumenDePublicacion(filas: FilaDePublicacion[]): {
  subidas: number;
  total: number;
  terminado: boolean;
  conErrores: boolean;
  motivo: string | null;
} {
  const enJuego = filas.filter((f) => f.estado === "publicando" || f.estado === "publicado" || f.estado === "error");
  const publicando = enJuego.filter((f) => f.estado === "publicando").length;
  const fallidos = enJuego.filter((f) => f.estado === "error");
  const primero = fallidos.find((f) => f.error) ?? null;
  let motivo: string | null = null;
  if (primero?.error) {
    motivo = primero.nombre ? `${primero.nombre} → ${primero.error}` : primero.error;
    if (fallidos.length > 1) motivo += ` (y ${fallidos.length - 1} más)`;
  }
  return {
    subidas: enJuego.length - publicando,
    total: enJuego.length,
    terminado: publicando === 0,
    conErrores: fallidos.length > 0,
    motivo,
  };
}

/** El poll se rindió: lo que no subió queda marcado, sin inventar un éxito. */
/**
 * Deshace el "publicando" cuando el pedido NI SIQUIERA salió.
 *
 * `marcarPublicando` corre antes de disparar, porque el worker puede empezar en cualquier
 * momento y la pantalla necesita el estado desde el primer segundo. Si el backend rechaza
 * —la tienda se desconectó, por ejemplo— no hay ningún trabajo en curso, y dejar el
 * lanzamiento en "Publicando" es mentirle a la persona: los spinners no terminan nunca y
 * el reintento arranca desde un estado falso
 * (ADR-2026-09-21-la-publicacion-rechazada-vuelve-a-borrador, review Greptile #127).
 *
 * Vuelve a "borrador" y a "pendiente", que es exactamente donde estaba: NO es un error,
 * es que no pasó nada. Lo ya publicado en corridas anteriores no se toca.
 */
export async function deshacerPublicando(lanzamientoId: string): Promise<void> {
  const ahora = new Date().toISOString();
  await supabase
    .from("lanzamientos_productos")
    .update({ estado: "pendiente", error: null, updated_at: ahora })
    .eq("lanzamiento_id", lanzamientoId)
    .eq("estado", "publicando");
  await supabase
    .from("lanzamientos")
    .update({ estado: "borrador", updated_at: ahora })
    .eq("id", lanzamientoId)
    .eq("estado", "publicando");
}

/**
 * ¿El backend sigue publicando algo de este lanzamiento? `lanzamientos_publicaciones` lo anota el worker
 * (ADR-2026-09-30-publicar-lanzamiento-a-tiendanube-bugs) y lo cierra él mismo si el job muere. Mientras diga
 * "publicando" (y no haga más de 30 min), la pantalla no lo marca como error
 * (ADR-2026-10-06-la-pantalla-no-se-rinde-mientras-se-publica). Si no se puede preguntar, false: se sigue como antes.
 */
export async function sigueEnElBackend(lanzamientoId: string): Promise<boolean> {
  const desde = new Date(Date.now() - 30 * 60_000).toISOString();
  const { data, error } = await supabase
    .from("lanzamientos_publicaciones")
    .select("lanzamiento_producto_id")
    .eq("lanzamiento_id", lanzamientoId)
    .eq("estado", "publicando")
    .gte("pedida_at", desde)
    .limit(1);
  if (error) return false;
  return (data ?? []).length > 0;
}

export async function marcarPublicacionIncompleta(lanzamientoId: string): Promise<void> {
  const ahora = new Date().toISOString();
  // Sólo lo que quedó "publicando": es lo que se mandó y no terminó. Lo destildado sigue
  // "pendiente" (no viajó) y no puede pasar a error por una publicación que no lo incluía
  // (ADR-2026-09-30-lo-tildado-es-lo-que-se-publica).
  await supabase
    .from("lanzamientos_productos")
    .update({ estado: "error", error: "No se confirmó la subida. Probá publicar de nuevo.", updated_at: ahora })
    .eq("lanzamiento_id", lanzamientoId)
    .eq("estado", "publicando");
  await supabase
    .from("lanzamientos")
    .update({ estado: "con_errores", updated_at: ahora })
    .eq("id", lanzamientoId);
}

// ── Sumar fotos a un producto ya cargado ───────────────────────────────────

/** El próximo orden libre en ese producto. */
async function siguienteOrden(lanzamientoProductoId: string): Promise<number> {
  const { data } = await supabase
    .from("lanzamientos_imagenes")
    .select("orden")
    .eq("lanzamiento_producto_id", lanzamientoProductoId)
    .order("orden", { ascending: false })
    .limit(1);
  const ultimo = ((data || []) as Array<{ orden: number | null }>)[0]?.orden;
  return ultimo != null ? ultimo + 1 : 0;
}

/** Suma imágenes YA generadas en Atlas a un producto del lanzamiento. */
export async function sumarGeneradasAProducto(
  lanzamientoProductoId: string,
  companyId: string,
  imagenes: Array<{ id: string; varianteId: string | null }>,
  /** Con las fotos por variante, nacen ya asignadas: si se asignaran después,
   *  entre el alta y el update la foto no se vería en ninguna tira. */
  lanzamientoVarianteId: string | null = null,
): Promise<{ agregadas: number; error: string | null }> {
  if (imagenes.length === 0) return { agregadas: 0, error: null };
  const desde = await siguienteOrden(lanzamientoProductoId);
  if (desde + imagenes.length > MAX_IMAGENES_POR_PRODUCTO) {
    return {
      agregadas: 0,
      error: `Un producto admite hasta ${MAX_IMAGENES_POR_PRODUCTO} fotos`,
    };
  }
  const { data, error } = await supabase
    .from("lanzamientos_imagenes")
    .upsert(
      imagenes.map((img, i) => ({
        lanzamiento_producto_id: lanzamientoProductoId,
        id_company: companyId,
        imagen_generada_id: img.id,
        variante_id: img.varianteId,
        lanzamiento_variante_id: lanzamientoVarianteId,
        orden: desde + i,
      })),
      { onConflict: "lanzamiento_producto_id,imagen_generada_id", ignoreDuplicates: true },
    )
    .select("id");
  if (error) {
    console.error("[lanzamientosApi] sumar generadas", error);
    return { agregadas: 0, error: "No se pudieron agregar las fotos" };
  }
  return { agregadas: (data || []).length, error: null };
}

/**
 * Sube un archivo del usuario y lo suma al producto.
 *
 * Va al MISMO lugar que una imagen generada —bucket `generadas`, ruta
 * `{id_company}/full/{archivo}`— para que el export firme la URL igual para las
 * dos y no haya un segundo camino. No se crea fila en `imagenes_generadas`: esa
 * tabla es el contrato de las generaciones y esto no lo generó nadie.
 */
export async function subirImagenAProducto(
  lanzamientoProductoId: string,
  companyId: string,
  archivo: File,
  /** Ídem `sumarGeneradasAProducto`: nace en la variante, no se asigna después. */
  lanzamientoVarianteId: string | null = null,
  /**
   * El destino recibe video — `reglasDe(canal).conVideo`. Apagado, un mp4 se
   * rechaza acá y no en el publicador: una tienda lo descartaría igual, y
   * enterarse al subir es mejor que enterarse al publicar.
   */
  aceptaVideo = false,
): Promise<{ error: string | null }> {
  const desde = await siguienteOrden(lanzamientoProductoId);
  if (desde >= MAX_IMAGENES_POR_PRODUCTO) {
    return { error: `Un producto admite hasta ${MAX_IMAGENES_POR_PRODUCTO} fotos` };
  }

  const esVideo = esArchivoDeVideo(archivo);
  if (esVideo && !aceptaVideo) {
    return { error: "Este destino no recibe videos" };
  }

  let file = archivo;
  if (esVideo) {
    if (file.size > MAX_BYTES_VIDEO) {
      return { error: mensajeVideoGrande() };
    }
  } else {
    try {
      assertSupportedUploadFormat(file);
      // HEIC/HEIF del iPhone y AVIF no los abre ni el navegador ni Tiendanube.
      // Un video NO pasa por acá: `normalizeImageForUpload` intenta decodificarlo
      // como imagen y falla.
      file = await normalizeImageForUpload(file);
    } catch (e) {
      return { error: e instanceof Error ? e.message : "Ese archivo no se puede usar" };
    }
  }

  const ext = (file.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "");
  const fileName = `subida_${crypto.randomUUID()}.${ext || "jpg"}`;
  const { error: eSubida } = await supabase.storage
    .from(GENERADAS_BUCKET)
    .upload(`${companyId}/full/${fileName}`, file, {
      contentType: file.type || undefined,
      upsert: false,
    });
  if (eSubida) {
    console.error("[lanzamientosApi] subir imagen", eSubida);
    return { error: esVideo ? "No se pudo subir el video" : "No se pudo subir la imagen" };
  }

  // El póster va DESPUÉS del archivo y sin frenar la subida si falla: sin él el
  // video se ve con un ícono en vez de un cuadro, que es feo pero no roto.
  if (esVideo) {
    const poster = await posterDeVideo(file);
    if (poster) {
      const { error: ePoster } = await supabase.storage
        .from(GENERADAS_BUCKET)
        .upload(`${companyId}/opt/${nombreDelPoster(fileName)}`, poster, {
          contentType: "image/png",
          upsert: true,
        });
      if (ePoster) console.warn("[lanzamientosApi] póster del video", ePoster);
    }
  }

  const { error } = await supabase.from("lanzamientos_imagenes").insert({
    lanzamiento_producto_id: lanzamientoProductoId,
    id_company: companyId,
    file_name: fileName,
    lanzamiento_variante_id: lanzamientoVarianteId,
    orden: desde,
  });
  if (error) {
    console.error("[lanzamientosApi] alta de imagen subida", error);
    return {
      error: esVideo
        ? "El video se subió pero no se pudo agregar"
        : "La imagen se subió pero no se pudo agregar al producto",
    };
  }
  return { error: null };
}

/** Una imagen generada de la empresa, para el selector. */
export interface GeneradaParaElegir {
  id: string;
  fileName: string;
  varianteId: string | null;
  thumbUrl: string;
  /** Se generó a partir del producto que se está editando. */
  delProducto: boolean;
  /** Para dibujarle el ícono de reproducir y saber qué formato arma. */
  esVideo: boolean;
}

/**
 * Imágenes generadas de la empresa para sumarle a un producto. Las que se
 * generaron con ESTE producto van primero: son las que casi siempre se buscan.
 */
export async function fetchGeneradasParaElegir(
  companyId: string,
  varianteIdsDelProducto: string[],
  yaUsadas: string[] = [],
  /**
   * Traer también los videos — `reglasDe(canal).conVideo`.
   *
   * Apagado por default porque el selector nació para fichas de producto, y una
   * tienda descarta un mp4 como foto: ofrecerlo ahí deja armar un lanzamiento
   * con material que el publicador va a tirar.
   */
  conVideo = false,
  /**
   * Sólo las creaciones de esta variante: el filtro de Producto del selector. Va en la
   * query, como en Creaciones, y no sobre las 120 de abajo: un producto con creaciones
   * viejas aparecería vacío (ADR-2026-10-20-filtro-de-producto-en-el-selector-de-creaciones).
   */
  soloVariante: string | null = null,
): Promise<GeneradaParaElegir[]> {
  let query = supabase
    .from("imagenes_generadas")
    .select("id, file_name, variante_id, tipo")
    .eq("id_company", companyId)
    .eq("active", true);
  if (soloVariante) query = query.eq("variante_id", soloVariante);
  const { data, error } = await query.order("created_at", { ascending: false }).limit(120);
  if (error) {
    console.error("[lanzamientosApi] generadas para elegir", error);
    return [];
  }
  const usadas = new Set(yaUsadas);
  const delProducto = new Set(varianteIdsDelProducto);
  const sirve = (fileName: string, tipo: string) => {
    const video = tipo.startsWith("video") || esNombreDeVideo(fileName);
    if (video) return conVideo;
    return /\.(png|jpe?g|webp)$/i.test(fileName);
  };
  const filas = ((data || []) as any[])
    .filter((r) => r.file_name && sirve(r.file_name, String(r.tipo ?? "")) && !usadas.has(r.id))
    .map((r) => ({
      id: r.id as string,
      fileName: r.file_name as string,
      varianteId: (r.variante_id as string) ?? null,
      thumbUrl: "",
      delProducto: r.variante_id != null && delProducto.has(r.variante_id),
      esVideo: esNombreDeVideo(r.file_name as string) || String(r.tipo ?? "").startsWith("video"),
    }));

  filas.sort((a, b) => Number(b.delProducto) - Number(a.delProducto));
  const recortadas = filas.slice(0, 60);
  return Promise.all(
    recortadas.map(async (f) => ({ ...f, thumbUrl: await firmarThumb(companyId, f.fileName) })),
  );
}


// ── Lo que usa el AGENTE (ADR-2026-09-09-el-agente-edita-lanzamientos) ──────

/**
 * Sobre qué lanzamiento trabaja el agente: el que le pasaron, el que el usuario tiene
 * abierto (llega por el contexto del chat), o el borrador más reciente.
 *
 * El borrador y no el último a secas: uno ya publicado no se edita, y ofrecerlo llevaría a
 * cambios que no van a ningún lado.
 */
export async function lanzamientoParaElAgente(
  companyId: string,
  idPedido?: string | null,
): Promise<{ id: string; nombre: string; estado: EstadoLanzamiento } | null> {
  // Sin id explícito, el que el usuario tiene abierto en pantalla; recién después, el
  // borrador más reciente. Con dos borradores, editar el equivocado son cuarenta fichas
  // para revertir a mano.
  const enPantalla =
    typeof window !== "undefined" ? idDeLanzamientoEnRuta(window.location?.pathname) : null;
  const id = idPedido || enPantalla;
  const base = supabase
    .from("lanzamientos")
    .select("id, nombre, estado")
    .eq("id_company", companyId);
  const { data } = id
    ? await base.eq("id", id).limit(1)
    : await base.eq("estado", "borrador").order("created_at", { ascending: false }).limit(1);
  const fila = ((data || []) as any[])[0];
  return fila
    ? { id: fila.id as string, nombre: (fila.nombre as string) ?? "", estado: fila.estado as EstadoLanzamiento }
    : null;
}

/** Un cambio ya resuelto: qué fila, qué columna, qué valor. */
export interface CambioAplicable {
  lanzamientoProductoId: string;
  /** Nombre del campo en el vocabulario de la ficha (`precio`, `seoTitulo`…). */
  campo: string;
  valor: string;
}

/**
 * Escribe los cambios, agrupados por producto: un solo UPDATE por fila aunque el diff
 * toque cinco campos de la misma. Devuelve cuántas filas se actualizaron.
 *
 * Reusa `actualizarFicha`, que ya sabe qué columna es cada campo y cuáles son numéricas —
 * escribir el UPDATE a mano acá sería duplicar ese mapeo y que se desincronicen.
 */
export async function aplicarCambiosDeFicha(
  cambios: CambioAplicable[],
): Promise<{ productos: number }> {
  const porProducto = new Map<string, CamposDeFicha>();
  for (const c of cambios) {
    const previo = porProducto.get(c.lanzamientoProductoId) ?? {};
    porProducto.set(c.lanzamientoProductoId, { ...previo, [c.campo]: c.valor } as CamposDeFicha);
  }
  for (const [id, campos] of porProducto) await actualizarFicha(id, campos);
  return { productos: porProducto.size };
}

/** Una fila BORRADOR: datos sin producto del catálogo detrás. */
export interface FilaBorrador {
  nombre?: string;
  descripcion?: string;
  precio?: string;
  costo?: string;
  stock?: string;
  sku?: string;
  codigoBarras?: string;
  marca?: string;
  tags?: string;
  peso?: string;
  alto?: string;
  ancho?: string;
  profundidad?: string;
  seoTitulo?: string;
  seoDescripcion?: string;
}

/**
 * Crea filas del lanzamiento SIN producto del catálogo
 * (ADR-2026-09-09-productos-de-lanzamiento-sin-catalogo). Es lo que deja cargar un Excel
 * de datos y que las fotos lleguen después, cuando el producto real aparezca con ese SKU.
 *
 * `accion: "create"` porque, cuando se publique, va a haber que crearlo en la tienda: no
 * existe allá todavía.
 */
export async function agregarFilasBorrador(
  lanzamientoId: string,
  companyId: string,
  filas: FilaBorrador[],
): Promise<{ creados: number; error: string | null }> {
  if (filas.length === 0) return { creados: 0, error: null };
  const { count } = await supabase
    .from("lanzamientos_productos")
    .select("id", { count: "exact", head: true })
    .eq("lanzamiento_id", lanzamientoId);
  const yaHay = count ?? 0;
  const lugar = MAX_PRODUCTOS_POR_LANZAMIENTO - yaHay;
  if (lugar <= 0) {
    return { creados: 0, error: `El lanzamiento ya tiene ${MAX_PRODUCTOS_POR_LANZAMIENTO} productos` };
  }
  const aCrear = filas.slice(0, lugar);
  const numero = (v: string | undefined) => (v === undefined ? null : aNumerico(v));
  const { error } = await supabase.from("lanzamientos_productos").insert(
    aCrear.map((f, i) => ({
      lanzamiento_id: lanzamientoId,
      id_company: companyId,
      producto_id: null,
      orden: yaHay + i,
      accion: "create",
      nombre: f.nombre ?? null,
      descripcion: f.descripcion ?? null,
      sku: f.sku ?? null,
      codigo_barras: f.codigoBarras ?? null,
      marca: f.marca ?? null,
      tags: f.tags ?? null,
      seo_titulo: f.seoTitulo ?? null,
      seo_descripcion: f.seoDescripcion ?? null,
      precio: numero(f.precio),
      costo: numero(f.costo),
      stock: numero(f.stock),
      peso: numero(f.peso),
      alto: numero(f.alto),
      ancho: numero(f.ancho),
      profundidad: numero(f.profundidad),
    })),
  );
  if (error) {
    console.error("[lanzamientosApi] filas borrador", error);
    return { creados: 0, error: "No se pudieron agregar los productos al lanzamiento" };
  }
  await supabase
    .from("lanzamientos")
    .update({ updated_at: new Date().toISOString() })
    .eq("id", lanzamientoId);
  return {
    creados: aCrear.length,
    error:
      filas.length > aCrear.length
        ? `Entraron ${aCrear.length}: el lanzamiento admite hasta ${MAX_PRODUCTOS_POR_LANZAMIENTO} productos`
        : null,
  };
}


/**
 * Agrega productos del CATÁLOGO al lanzamiento, sin imágenes. Es lo que usa el agente
 * cuando el usuario dice "sumá estos productos al lanzamiento": las fotos llegan después
 * por el camino de siempre (ADR-2026-09-09-el-agente-edita-lanzamientos).
 *
 * Los que ya estaban no se duplican ni se pisan: se reportan como "ya estaban".
 */
export async function agregarProductosDelCatalogo(
  lanzamientoId: string,
  companyId: string,
  productoIds: string[],
): Promise<{ agregados: number; yaEstaban: number; error: string | null }> {
  const pedidos = [...new Set(productoIds.filter(Boolean))];
  if (pedidos.length === 0) return { agregados: 0, yaEstaban: 0, error: null };

  const leerCatalogo = async (algunosIds: string[]) => {
    // El filtro por empresa NO es decorativo: es lo que impide que un id de otra cuenta
    // entre a un lanzamiento de ésta. Vale igual para los ids traducidos de abajo.
    const { data, error } = await leerCatalogoParaLaFicha(algunosIds, companyId);
    return { filas: (data || []) as any[], error };
  };

  const { filas: comoProducto, error: eCat } = await leerCatalogo(pedidos);
  if (eCat) {
    console.error("[lanzamientosApi] catálogo", eCat);
    return { agregados: 0, yaEstaban: 0, error: "No se pudo leer el catálogo" };
  }

  // Lo que el CHAT llama "producto" es una VARIANTE: su catálogo, `pick_product` y los
  // adjuntos salen de la tabla `variantes` (`productsApi.fetchUserProducts`). Un
  // lanzamiento, en cambio, junta PRODUCTOS. Buscando sólo por `productos.id`, todo lo
  // que mandaba el agente caía afuera y la tool devolvía "agregué 0" sin decir por qué
  // (visto el 21/09 con "zapatillas")
  // (ADR-2026-09-21-agregar-al-lanzamiento-acepta-el-id-de-la-variante).
  const encontrados = new Set(comoProducto.map((r) => r.id as string));
  const sinResolver = pedidos.filter((id) => !encontrados.has(id));
  let catalogo = comoProducto;
  let ids = pedidos.filter((id) => encontrados.has(id));
  if (sinResolver.length > 0) {
    const mapa = await productoPorVariante(sinResolver);
    const traducidos = [...new Set([...mapa.values()])].filter((id) => !encontrados.has(id));
    if (traducidos.length > 0) {
      const { filas } = await leerCatalogo(traducidos);
      const deOtraEmpresa = new Set(filas.map((r) => r.id as string));
      catalogo = [...comoProducto, ...filas];
      ids = [...ids, ...traducidos.filter((id) => deOtraEmpresa.has(id))];
    }
  }
  const existenEnCatalogo = new Set(catalogo.map((r) => r.id as string));

  const { data: yaEnLanzamiento } = await supabase
    .from("lanzamientos_productos")
    .select("producto_id")
    .eq("lanzamiento_id", lanzamientoId)
    .in("producto_id", ids);
  const yaEstan = new Set(((yaEnLanzamiento || []) as any[]).map((r) => r.producto_id as string));

  const aAgregar = ids.filter((id) => existenEnCatalogo.has(id) && !yaEstan.has(id));
  if (aAgregar.length === 0) {
    // "Agregué 0" no es una respuesta: sin el motivo, ni la persona ni el agente saben si
    // ya estaban, si no se encontraron, o si algo falló.
    const noEncontrados = pedidos.length - ids.length;
    return {
      agregados: 0,
      yaEstaban: yaEstan.size,
      error:
        yaEstan.size > 0 && noEncontrados === 0
          ? yaEstan.size === 1
            ? "Ese producto ya estaba en el lanzamiento"
            : `Esos ${yaEstan.size} productos ya estaban en el lanzamiento`
          : "No encontré esos productos en el catálogo de la marca",
    };
  }

  const { count } = await supabase
    .from("lanzamientos_productos")
    .select("id", { count: "exact", head: true })
    .eq("lanzamiento_id", lanzamientoId);
  const yaHay = count ?? 0;
  if (yaHay + aAgregar.length > MAX_PRODUCTOS_POR_LANZAMIENTO) {
    return {
      agregados: 0,
      yaEstaban: yaEstan.size,
      error: `Un lanzamiento admite hasta ${MAX_PRODUCTOS_POR_LANZAMIENTO} productos`,
    };
  }

  const { error } = await supabase
    .from("lanzamientos_productos")
    .insert(
      aAgregar.map((id, i) =>
        fichaInicialDesdeCatalogo(lanzamientoId, companyId, catalogo, id, yaHay + i),
      ),
    );
  if (error) {
    console.error("[lanzamientosApi] agregar del catálogo", error);
    return { agregados: 0, yaEstaban: yaEstan.size, error: "No se pudieron agregar los productos" };
  }
  await supabase
    .from("lanzamientos")
    .update({ updated_at: new Date().toISOString() })
    .eq("id", lanzamientoId);
  return { agregados: aAgregar.length, yaEstaban: yaEstan.size, error: null };
}

/**
 * La copia del lanzamiento en pantalla con los datos de Mercado Libre que acaba de guardar la ficha de un
 * producto. Sin esto, el resumen de la ficha y "Publicar" seguían viendo los de cuando se abrió (prueba
 * del 2/10: "falta la categoría" con la categoría ya elegida).
 */
export function conDatosMl<T extends { productos_: ProductoDeLanzamiento[] }>(
  detalle: T | null,
  lanzamientoProductoId: string,
  datos: DatosMlDeFicha,
): T | null {
  if (!detalle) return detalle;
  return { ...detalle, productos_: detalle.productos_.map((p) => (p.id === lanzamientoProductoId ? { ...p, mercadolibre: datos } : p)) };
}
